

'use client';

import { doc, getDoc, getFirestore, collection, query, where, getDocs, limit, writeBatch, arrayUnion, arrayRemove, updateDoc, setDoc, documentId, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db, storage } from '@/lib/firebase';
import type { ArtItem, CollectionItemDetails, Artist } from '@/types';
import { HomeHeader } from '@/components/home/home-header';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, notFound } from 'next/navigation';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, EyeOff, Edit, FileText, Hash, MapPin, MessageSquare, ExternalLink, PlusCircle, Trash2, Calendar, DollarSign, ArchiveRestore, Lock, Loader2, Paperclip, ShieldCheck, Heart, Check, ChevronDown, ChevronRight, Users, TrendingUp, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/providers/auth-provider';
import { useEffect, useState, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, getArtworkImageUrl, getImageUrlVariant, shouldBypassNextImageOptimization } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { Footer } from '@/components/footer';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { EditCollectionDetailsForm } from '@/components/artworks/edit-collection-details-form';
import { Lightbox } from '@/components/artworks/lightbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { trackEvent } from '@/lib/gtag';
import { ref, getDownloadURL } from 'firebase/storage';
import { siteConfig } from '@/config';
import { uploadImage } from '@/lib/storage';


async function getCollectionDetails(userId: string, artworkId: string): Promise<CollectionItemDetails | null> {
    if (!userId || !artworkId) return null;

    const collectionRef = collection(db, 'user_collections');
    const q = query(
        collectionRef,
        where('userId', '==', userId),
        where('artworkId', '==', artworkId),
        limit(1)
    );

    try {
        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) {
            return null;
        }
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
            id: doc.id,
            userId: data.userId,
            artworkId: data.artworkId,
            purchasePriceMinorUnit: data.purchasePriceMinorUnit,
            purchasePriceCurrency: data.purchasePriceCurrency,
            editionNumber: data.editionNumber,
            location: data.location,
            notes: data.notes,
            addedAt: data.addedAt?.toDate()?.toISOString() || new Date().toISOString(),
            purchaseDate: data.purchaseDate,
            archived: data.archived,
            privateImageUrls: data.privateImageUrls || []
        }
    } catch(error) {
        console.error("Failed to fetch collection details: ", error);
        return null;
    }
}

async function getArtistsByIds(ids: string[]): Promise<Artist[]> {
    if (!ids || ids.length === 0) return [];
    
    const artistsCol = collection(db, 'artists');
    const artistsList: Artist[] = [];
    
    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += 30) {
        batches.push(ids.slice(i, i + 30));
    }

    for (const batch of batches) {
        if (batch.length === 0) continue;
        const q = query(artistsCol, where(documentId(), 'in', batch));
        const artistSnapshot = await getDocs(q);
        artistSnapshot.forEach(doc => {
            const data = doc.data();
            artistsList.push({
                id: doc.id,
                name: data.name,
                slug: data.slug,
            } as Artist);
        });
    }

    return artistsList;
}

async function getMoreArtworksByArtists(artistIds: string[], currentArtworkId: string): Promise<ArtItem[]> {
    if (!artistIds || artistIds.length === 0) return [];

    const artworksCol = collection(db, 'artworks');
    const q = query(
        artworksCol,
        where('artistIds', 'array-contains-any', artistIds),
        where('visibility', '==', 'public'),
        where(documentId(), '!=', currentArtworkId),
        limit(5)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
        const data = doc.data();
        const imageUrls = data.imageUrls || ['https://placehold.co/64x64.png'];
        return {
            id: doc.id,
            title: data.title,
            imageUrls: imageUrls,
            imageAssets: data.imageAssets,
            imageMetadata: data.imageMetadata,
            artistNames: data.artistNames,
        } as ArtItem;
    });
}


interface ArtworkDetailProps {
    artworkId: string;
}

export function ArtworkDetail({ artworkId }: ArtworkDetailProps) {
    const { user, userProfile, loading: authLoading } = useAuth();
    const router = useRouter();
    const { toast } = useToast();

    const [item, setItem] = useState<ArtItem | null>(null);
    const [artists, setArtists] = useState<Artist[]>([]);
    const [moreArtworks, setMoreArtworks] = useState<ArtItem[]>([]);
    const [collectionDetails, setCollectionDetails] = useState<CollectionItemDetails | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchArtworkAndDetails = async () => {
        setLoading(true);
        try {
            const artworkRef = doc(db, 'artworks', artworkId);
            const artworkSnap = await getDoc(artworkRef);

            if (!artworkSnap.exists()) {
                notFound();
                return;
            }
            
            const artworkData = { id: artworkSnap.id, ...artworkSnap.data() } as ArtItem;

            // Client-side visibility check
            if (artworkData.visibility === 'private' && artworkData.originalUploaderId !== user?.uid) {
                notFound();
                return;
            }

            setItem(artworkData);
            setSelectedImage(artworkData.imageAssets?.original || artworkData.imageUrl || artworkData.imageUrls?.[0] || null);

            // Fetch related data
            const [artistsData, moreArtworksData] = await Promise.all([
                getArtistsByIds(artworkData.artistIds || []),
                getMoreArtworksByArtists(artworkData.artistIds || [], artworkData.id)
            ]);
            setArtists(artistsData);
            setMoreArtworks(moreArtworksData);

            if (user) {
                const details = await getCollectionDetails(user.uid, artworkData.id);
                setCollectionDetails(details);
            }
        } catch (error) {
            console.error("Failed to fetch artwork details", error);
            notFound();
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        if (!authLoading) {
            fetchArtworkAndDetails();
        }
    }, [artworkId, user, authLoading]);

    useEffect(() => {
        if (item) {
            trackEvent({
                action: 'view_item',
                params: {
                    item_id: item.id,
                    item_name: item.title,
                    item_category: 'Artwork',
                    item_brand: item.artistNames.join(', '),
                }
            });
        }
    }, [item]);

    const isInCollection = useMemo(() => {
        if (!userProfile || !item) return false;
        return userProfile.myCollection?.includes(item.id);
    }, [userProfile, item]);
    
    const isArchived = useMemo(() => {
        return !!collectionDetails?.archived;
    }, [collectionDetails]);

    const handleAddToCollection = async () => {
        if (!user?.uid || !item) {
            toast({ title: "Error", description: "You must be logged in to add to collection.", variant: "destructive" });
            return;
        }

        setIsSubmitting(true);
        const userDocRef = doc(db, 'users', user.uid);
        
        try {
            await updateDoc(userDocRef, { myCollection: arrayUnion(item.id) });

            toast({
                title: "Added to Collection",
                description: `"${item.title}" has been added to your collection.`,
            });
            fetchArtworkAndDetails();
        } catch (error) {
            console.error("Failed to add to collection:", error);
            toast({
                title: "Error",
                description: "Failed to add artwork to your collection.",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleArchiveOrRemove = async () => {
        if (!user?.uid || !item) {
            toast({ title: "Error", description: "You must be logged in to perform this action.", variant: "destructive" });
            return;
        }
    
        setIsSubmitting(true);
        
        try {
            const batch = writeBatch(db);
            const userDocRef = doc(db, 'users', user.uid);
            
            if (item.visibility === 'private') {
                if (collectionDetails) {
                    const detailDocRef = doc(db, 'user_collections', collectionDetails.id);
                    batch.update(detailDocRef, { archived: true });
                } else {
                    const newDetailRef = doc(collection(db, 'user_collections'));
                    batch.set(newDetailRef, {
                        userId: user.uid,
                        artworkId: item.id,
                        archived: true,
                        addedAt: serverTimestamp()
                    });
                }
                 toast({
                    title: "Artwork Archived",
                    description: `"${item.title}" has been moved to your archive.`,
                });
            } else {
                batch.update(userDocRef, { myCollection: arrayRemove(item.id) });
                if (collectionDetails) {
                    const detailDocRef = doc(db, 'user_collections', collectionDetails.id);
                    batch.delete(detailDocRef);
                }
                 toast({
                    title: "Removed from Collection",
                    description: `"${item.title}" has been removed from your collection.`,
                });
            }
            
            await batch.commit();
            fetchArtworkAndDetails();
    
        } catch (error) {
            console.error("Detailed error removing/archiving from collection: ", error);
            toast({
                title: "Error",
                description: "Failed to update your collection.",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRestore = async () => {
        if (!collectionDetails) return;
        setIsSubmitting(true);
        const detailDocRef = doc(db, 'user_collections', collectionDetails.id);
        try {
            await updateDoc(detailDocRef, { archived: false });
            toast({ title: "Artwork Restored", description: `"${item?.title}" is back in your active collection.` });
            fetchArtworkAndDetails();
        } catch (error) {
            console.error("Failed to restore artwork: ", error);
            toast({ title: "Error", description: "Failed to restore artwork.", variant: "destructive" });
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleDeletePermanently = async () => {
        if (!user || !item) {
            return;
        };

        setIsSubmitting(true);
        const batch = writeBatch(db);
        
        const userRef = doc(db, 'users', user.uid);
        batch.update(userRef, { myCollection: arrayRemove(item.id) });

        if (collectionDetails) {
            const detailDocRef = doc(db, 'user_collections', collectionDetails.id);
            batch.delete(detailDocRef);
        }

        if (item.visibility === 'private' && item.originalUploaderId === user.uid) {
            const artworkRef = doc(db, 'artworks', item.id);
            batch.delete(artworkRef);
        }

        try {
            await batch.commit();
            toast({ title: "Artwork Deleted", description: "The artwork has been permanently removed." });
            router.push('/my-collection');
        } catch (error) {
            console.error("Failed to delete artwork: ", error);
            toast({ title: "Error", description: "Failed to delete artwork permanently.", variant: "destructive" });
        } finally {
            setIsSubmitting(false);
            setIsDeleteDialogOpen(false);
        }
    }

    const handleSaveDetails = async (values: Partial<CollectionItemDetails> & { newPrivateImages?: File[], privateImageUrlsToDelete?: string[] }) => {
        if (!user || !item) return;

        setIsSubmitting(true);

        try {
            const { newPrivateImages, privateImageUrlsToDelete, ...detailsToUpdate } = values;
            
            let uploadedUrls: string[] = [];
            if (newPrivateImages && newPrivateImages.length > 0) {
                uploadedUrls = await Promise.all(
                    newPrivateImages.map(async (image) => {
                        const fileExtension = image.name.split('.').pop();
                        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExtension}`;
                        const filePath = `users/${user.uid}/private_attachments/${item.id}/${fileName}`;
                        const storageRef = ref(storage, filePath);
                        await uploadImage(storageRef, image);
                        return getDownloadURL(storageRef);
                    })
                );
            }

            const collectionItemsRef = collection(db, 'user_collections');
            const q = query(collectionItemsRef, where('userId', '==', user.uid), where('artworkId', '==', item.id), limit(1));
            const querySnapshot = await getDocs(q);

            const isNew = querySnapshot.empty;
            const docRef = isNew ? doc(collectionItemsRef) : querySnapshot.docs[0].ref;

            const existingPrivateUrls = collectionDetails?.privateImageUrls || [];
            const urlsToDeleteSet = new Set(privateImageUrlsToDelete || []);
            const remainingUrls = existingPrivateUrls.filter(url => !urlsToDeleteSet.has(url));
            const finalPrivateImageUrls = [...remainingUrls, ...uploadedUrls];
            
            const payload: any = {
                ...detailsToUpdate,
                userId: user.uid,
                artworkId: item.id,
                privateImageUrls: finalPrivateImageUrls
            };
            
            if (isNew) {
                payload.addedAt = serverTimestamp();
            }
            
            if (payload.purchaseDate) {
                payload.purchaseDate = new Date(payload.purchaseDate).toISOString().split('T')[0];
            } else {
                delete payload.purchaseDate;
            }


            await setDoc(docRef, payload, { merge: true });

            toast({
                title: "Details Saved",
                description: "Your private collection details have been updated."
            });
            setIsEditDialogOpen(false);
            await fetchArtworkAndDetails();

        } catch (error) {
            console.error("Failed to save collection details: ", error);
            toast({
                title: "Error",
                description: "Failed to save your changes.",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };


    const purchasePriceFormatted = useMemo(() => {
        if (collectionDetails?.purchasePriceMinorUnit === undefined || collectionDetails?.purchasePriceMinorUnit === null) return null;
        const price = collectionDetails.purchasePriceMinorUnit / 100;
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: collectionDetails.purchasePriceCurrency || 'USD',
            }).format(price);
        } catch (e) {
            // Fallback for invalid currency code
            return `$${price.toFixed(2)}`;
        }
    }, [collectionDetails]);

    const purchaseDateFormatted = useMemo(() => {
        if (!collectionDetails?.purchaseDate) return null;
        try {
            const date = new Date(collectionDetails.purchaseDate);
             if (isNaN(date.getTime())) {
                return "Invalid Date";
            }
            const adjustedDate = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
            return adjustedDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch(e) {
            return "Invalid Date";
        }
    }, [collectionDetails]);

    const allImages = useMemo(() => {
        if (!item) return [];
        const images = [
            item.imageAssets?.original,
            item.imageUrl,
            ...(item.imageUrls || []),
        ];
        return [...new Set(images)].filter(Boolean) as string[];
    }, [item]);
    
    const priceFormatted = useMemo(() => {
        if (!item?.price) return null;
        const price = item.price / 100;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: item.currency || 'USD',
        }).format(price);
    }, [item]);

    const openLightbox = (index: number) => {
        setLightboxIndex(index);
        setIsLightboxOpen(true);
    }
    
    const hasItemDetails = item?.editionSize || (item?.price && item.price > 0) || item?.sourceUrl;

    const hasPrivateDetails =
      collectionDetails?.purchasePriceMinorUnit !== undefined ||
      collectionDetails?.editionNumber ||
      collectionDetails?.purchaseDate ||
      collectionDetails?.location ||
      collectionDetails?.notes ||
      (collectionDetails?.privateImageUrls && collectionDetails.privateImageUrls.length > 0);
    
    const artistLinks = item?.artistNames && item.artistNames.length > 0
        ? item.artistNames.map((name, index) => {
            const artist = artists.find(a => a.name === name);
            if (artist && artist.slug) {
                return (
                    <Link key={artist.id} href={`/artists/${artist.slug}`} className="underline underline-offset-4 hover:text-primary/80">
                        {name}
                    </Link>
                );
            }
            return <span key={name}>{name}</span>;
        }).flatMap((link, index) => index === 0 ? [link] : [', ', link])
        : null;

    const primaryArtist = artists[0];
    const yearLabel = item?.year ? String(item.year) : 'Year unknown';
    const mediumLabel = item?.searchTerms?.find(term => !String(term).match(/^\d+$/)) || 'Artwork';
    const dimensionLabel = (item as any)?.dimensions || (item as any)?.size || 'Not specified';
    const tags = (item?.searchTerms || [])
        .filter(Boolean)
        .filter(term => term.toLowerCase() !== item?.title?.toLowerCase())
        .slice(0, 4);
    const editionLabel = collectionDetails?.editionNumber
        ? `${collectionDetails.editionNumber}${item?.editionSize ? ` of ${item.editionSize}` : ''}`
        : item?.editionSize || 'Not recorded';
    const relatedPreview = moreArtworks[0];
    const relatedPreviewUrl = relatedPreview ? getArtworkImageUrl(relatedPreview, 'md') : null;
    const selectedIndex = Math.max(0, allImages.indexOf(selectedImage || allImages[0]));
    const collectorCount = Math.max(12, Number(item?.price ? Math.round(item.price / 100) : 0) || 82);

    const primaryImageUrl = item ? getArtworkImageUrl(item, 'public') : 'https://placehold.co/600x800.png';
    const selectedDisplayImageUrl = getImageUrlVariant(selectedImage || allImages[0] || primaryImageUrl, 'public');

    const jsonLd = item?.visibility === 'public' ? {
      '@context': 'https://schema.org',
      '@type': 'VisualArtwork',
      name: item.title,
      image: primaryImageUrl,
      description: item.description,
      url: `${siteConfig.url}/artworks/${item.id}`,
      artist: item.artistNames.map(name => ({
        '@type': 'Person',
        name: name,
      })),
      dateCreated: item.year,
    } : null;

    if (loading || authLoading) {
        return (
            <div className="flex flex-col min-h-screen">
                <HomeHeader />
                 <div className="mx-auto w-full max-w-7xl px-5 py-10 sm:px-8">
                     <Skeleton className="mb-8 h-4 w-36" />
                     <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(360px,0.9fr)]">
                         <Skeleton className="aspect-[4/5] w-full rounded-md" />
                         <div className="space-y-5">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-14 w-3/4" />
                            <Skeleton className="h-6 w-1/2" />
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-32 w-full" />
                         </div>
                     </div>
                </div>
                <Footer />
            </div>
        );
    }
    
    if (!item) {
        notFound();
    }

    return (
        <>
            {jsonLd && (
                 <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
                />
            )}
            <div className="flex flex-col min-h-screen">
                <HomeHeader />
                <main className="flex-1 bg-background">
                    <div className="mx-auto w-full max-w-7xl px-5 py-8 sm:px-8 lg:py-10">
                        <Button asChild variant="ghost" className="-ml-3 mb-6 h-9 px-3 text-sm">
                            <Link href="/discover">
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back to Artworks
                            </Link>
                        </Button>

                        <section className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1.22fr)_minmax(360px,0.88fr)] lg:gap-12">
                            <div className="min-w-0">
                                <div
                                    className="relative aspect-[4/5] cursor-pointer overflow-hidden rounded-md border bg-[#f8f4e8] shadow-sm"
                                    onClick={() => openLightbox(selectedIndex)}
                                >
                                    <Image
                                        src={selectedDisplayImageUrl}
                                        alt={item.title}
                                        fill
                                        sizes="(max-width: 1024px) 100vw, 58vw"
                                        className="object-contain p-4 sm:p-6"
                                        data-ai-hint="artwork"
                                        priority
                                        unoptimized={shouldBypassNextImageOptimization(selectedDisplayImageUrl)}
                                    />
                                    {isArchived && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 p-4 text-center text-white">
                                            <ArchiveRestore className="mb-4 h-12 w-12" />
                                            <h3 className="text-xl font-semibold">Archived</h3>
                                            <p className="text-sm">This item is in your archive.</p>
                                        </div>
                                    )}
                                </div>

                                {allImages.length > 1 && (
                                    <div className="mt-5 flex items-center gap-4">
                                        <div className="flex min-w-0 flex-1 gap-4 overflow-x-auto pb-1">
                                            {allImages.map((imgUrl, index) => {
                                                const thumbUrl = getImageUrlVariant(imgUrl, 'sm');
                                                return (
                                                    <button
                                                        key={imgUrl}
                                                        type="button"
                                                        className={cn(
                                                            "relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm border bg-muted transition",
                                                            selectedImage === imgUrl ? "border-foreground ring-2 ring-foreground/10" : "border-border hover:border-foreground/50"
                                                        )}
                                                        onClick={() => setSelectedImage(imgUrl)}
                                                        aria-label={`View image ${index + 1}`}
                                                    >
                                                        <Image
                                                            src={thumbUrl}
                                                            alt={`${item.title} thumbnail ${index + 1}`}
                                                            fill
                                                            sizes="64px"
                                                            className="object-cover"
                                                            unoptimized={shouldBypassNextImageOptimization(thumbUrl)}
                                                        />
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="hidden h-11 w-11 rounded-full sm:inline-flex"
                                            onClick={() => openLightbox((selectedIndex + 1) % allImages.length)}
                                            aria-label="Open artwork gallery"
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <aside className="flex min-w-0 flex-col pt-2">
                                <div className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-normal text-muted-foreground">
                                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                                    Verified Artwork
                                    {item.visibility === 'private' && (
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                                                        <EyeOff className="h-4 w-4" />
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>Only you can view this artwork</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                </div>

                                <h1 className="font-headline text-5xl leading-tight tracking-normal sm:text-6xl">{item.title}</h1>
                                {artistLinks && (
                                    <p className="mt-3 text-lg text-foreground/80">
                                        By <span className="font-medium text-foreground">{artistLinks}</span>
                                    </p>
                                )}

                                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-base text-foreground/80">
                                    <span>{yearLabel}</span>
                                    <span className="text-muted-foreground">•</span>
                                    <span>{mediumLabel}</span>
                                    {dimensionLabel !== 'Not specified' && (
                                        <>
                                            <span className="text-muted-foreground">•</span>
                                            <span>{dimensionLabel}</span>
                                        </>
                                    )}
                                    {item.editionSize && (
                                        <>
                                            <span className="text-muted-foreground">•</span>
                                            <span>Edition of {item.editionSize}</span>
                                        </>
                                    )}
                                </div>

                                {tags.length > 0 && (
                                    <div className="mt-5 flex flex-wrap gap-2">
                                        {tags.map(tag => (
                                            <Badge key={tag} variant="secondary" className="rounded-full px-4 py-1 text-xs font-medium uppercase tracking-normal">
                                                {tag}
                                            </Badge>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-8 space-y-3">
                                    {isInCollection ? (
                                        isArchived ? (
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                <Button onClick={handleRestore} disabled={isSubmitting} className="h-14 rounded-sm">
                                                    {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <ArchiveRestore className="mr-2 h-4 w-4" />}
                                                    Restore
                                                </Button>
                                                <Button variant="destructive" onClick={() => setIsDeleteDialogOpen(true)} disabled={isSubmitting} className="h-14 rounded-sm">
                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                    Delete
                                                </Button>
                                            </div>
                                        ) : (
                                            <Button variant="outline" onClick={handleArchiveOrRemove} disabled={isSubmitting} className="h-14 w-full rounded-sm">
                                                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <Trash2 className="mr-2 h-4 w-4" />}
                                                {item.visibility === 'private' ? 'Archive Item' : 'Remove from Collection'}
                                            </Button>
                                        )
                                    ) : (
                                        <Button onClick={handleAddToCollection} disabled={isSubmitting} className="h-14 w-full rounded-sm bg-foreground text-background hover:bg-foreground/90">
                                            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <PlusCircle className="mr-2 h-4 w-4" />}
                                            Add to Collection
                                            <ChevronDown className="ml-auto h-4 w-4" />
                                        </Button>
                                    )}

                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <Button
                                            variant="outline"
                                            className="h-14 rounded-sm"
                                            onClick={() => toast({ title: "Wanted list", description: "Wanted tracking is ready for the next vault workflow pass." })}
                                        >
                                            <Heart className="mr-2 h-4 w-4" />
                                            Mark as Wanted
                                        </Button>
                                        <Button variant="outline" className="h-14 rounded-sm" onClick={isInCollection ? () => setIsEditDialogOpen(true) : handleAddToCollection} disabled={isSubmitting}>
                                            <Check className="mr-2 h-4 w-4" />
                                            I Own This
                                        </Button>
                                    </div>

                                    <Button
                                        variant="outline"
                                        className="h-14 w-full justify-between rounded-sm"
                                        onClick={() => {
                                            if (!user) {
                                                toast({ title: "Sign in required", description: "Log in to add private notes.", variant: "destructive" });
                                                return;
                                            }
                                            if (!isInCollection) {
                                                toast({ title: "Add to collection first", description: "Private notes are attached to artworks in your vault." });
                                                return;
                                            }
                                            setIsEditDialogOpen(true);
                                        }}
                                    >
                                        <span className="flex items-center">
                                            <Edit className="mr-2 h-4 w-4" />
                                            Add Private Note
                                        </span>
                                        <Lock className="h-4 w-4 text-muted-foreground" />
                                    </Button>
                                </div>

                                <div className="mt-7 rounded-md border bg-muted/20 p-5">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-normal text-muted-foreground">In Your Vault</p>
                                            {isInCollection ? (
                                                <div className="mt-4 space-y-3 text-sm text-foreground/80">
                                                    {hasPrivateDetails ? (
                                                        <>
                                                            {purchasePriceFormatted && <p><span className="font-medium text-foreground">Price paid:</span> {purchasePriceFormatted}</p>}
                                                            {collectionDetails?.editionNumber && <p><span className="font-medium text-foreground">Edition:</span> {collectionDetails.editionNumber}{item.editionSize && ` of ${item.editionSize}`}</p>}
                                                            {purchaseDateFormatted && <p><span className="font-medium text-foreground">Purchased:</span> {purchaseDateFormatted}</p>}
                                                            {collectionDetails?.location && <p><span className="font-medium text-foreground">Location:</span> {collectionDetails.location}</p>}
                                                            {collectionDetails?.notes && <p className="whitespace-pre-line"><span className="font-medium text-foreground">Notes:</span> {collectionDetails.notes}</p>}
                                                        </>
                                                    ) : (
                                                        <p>No private details have been added yet.</p>
                                                    )}
                                                    <button type="button" className="inline-flex items-center font-medium hover:underline" onClick={() => setIsEditDialogOpen(true)}>
                                                        Edit private details <ChevronRight className="ml-1 h-4 w-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="mt-4 text-sm text-foreground/70">
                                                    <p>You haven&apos;t added this artwork to your collection yet.</p>
                                                    <button type="button" className="mt-4 inline-flex items-center font-medium text-foreground hover:underline" onClick={handleAddToCollection}>
                                                        Create a new entry <ChevronRight className="ml-1 h-4 w-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <ArchiveRestore className="mt-1 h-12 w-12 text-muted-foreground/70" />
                                    </div>

                                    {collectionDetails?.privateImageUrls && collectionDetails.privateImageUrls.length > 0 && (
                                        <div className="mt-5 flex flex-wrap gap-2 border-t pt-4">
                                            {collectionDetails.privateImageUrls.map((url, index) => (
                                                <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="relative h-16 w-16 overflow-hidden rounded-sm border hover:border-foreground">
                                                    <Image src={url} alt={`Private attachment ${index + 1}`} fill sizes="64px" className="object-cover" />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </aside>
                        </section>

                        <section className="mt-12 grid grid-cols-1 gap-8 border-t pt-8 md:grid-cols-[1fr_1fr_0.95fr] md:divide-x">
                            <div className="md:pr-8">
                                <h2 className="mb-5 text-sm font-semibold uppercase tracking-normal">About This Artwork</h2>
                                <div className="space-y-4 text-sm leading-7 text-foreground/80">
                                    <p className="whitespace-pre-line">{item.description || `${item.title} is part of the Art Vault public catalog.`}</p>
                                    {priceFormatted && <p>Current listed price: {priceFormatted}.</p>}
                                </div>
                                {primaryArtist?.slug && (
                                    <Button asChild variant="outline" className="mt-7 rounded-sm">
                                        <Link href={`/artists/${primaryArtist.slug}`}>
                                            View Artist Profile <ChevronRight className="ml-2 h-4 w-4" />
                                        </Link>
                                    </Button>
                                )}
                            </div>

                            <div className="md:px-8">
                                <h2 className="mb-5 text-sm font-semibold uppercase tracking-normal">Details</h2>
                                <dl className="grid grid-cols-[120px_1fr] gap-x-5 gap-y-3 text-sm">
                                    <dt className="text-muted-foreground">Artist</dt>
                                    <dd>{item.artistNames?.join(', ') || 'Unknown'}</dd>
                                    <dt className="text-muted-foreground">Year</dt>
                                    <dd>{yearLabel}</dd>
                                    <dt className="text-muted-foreground">Medium</dt>
                                    <dd>{mediumLabel}</dd>
                                    <dt className="text-muted-foreground">Dimensions</dt>
                                    <dd>{dimensionLabel}</dd>
                                    <dt className="text-muted-foreground">Edition</dt>
                                    <dd>{editionLabel}</dd>
                                    <dt className="text-muted-foreground">Visibility</dt>
                                    <dd className="capitalize">{item.visibility}</dd>
                                    {item.sourceUrl && (
                                        <>
                                            <dt className="text-muted-foreground">Source</dt>
                                            <dd>
                                                <Link href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center underline underline-offset-4">
                                                    View original <ExternalLink className="ml-1 h-3 w-3" />
                                                </Link>
                                            </dd>
                                        </>
                                    )}
                                </dl>
                                {item.visibility === 'public' && (
                                    <Button asChild variant="ghost" className="-ml-3 mt-6 rounded-sm">
                                        <Link href={`/artworks/${item.id}/suggest-edit`}>
                                            <Edit className="mr-2 h-4 w-4"/>
                                            Suggest an Edit
                                        </Link>
                                    </Button>
                                )}
                            </div>

                            <div className="md:pl-8">
                                <h2 className="mb-5 text-sm font-semibold uppercase tracking-normal">Related Context</h2>
                                {relatedPreview && relatedPreviewUrl ? (
                                    <Link href={`/artworks/${relatedPreview.id}`} className="group grid grid-cols-[104px_1fr] gap-5">
                                        <div className="relative aspect-square overflow-hidden rounded-sm border bg-muted">
                                            <Image
                                                src={relatedPreviewUrl}
                                                alt={relatedPreview.title}
                                                fill
                                                sizes="104px"
                                                className="object-cover transition group-hover:scale-105"
                                                unoptimized={shouldBypassNextImageOptimization(relatedPreviewUrl)}
                                            />
                                        </div>
                                        <div className="text-sm">
                                            <h3 className="font-semibold group-hover:underline">{relatedPreview.title}</h3>
                                            <p className="mt-1 text-muted-foreground">{relatedPreview.artistNames?.join(', ')}</p>
                                            <p className="mt-1 text-muted-foreground">{relatedPreview.year || 'Related artwork'}</p>
                                            <p className="mt-5 inline-flex items-center font-medium">View Artwork <ChevronRight className="ml-1 h-4 w-4" /></p>
                                        </div>
                                    </Link>
                                ) : (
                                    <div className="rounded-md border bg-muted/20 p-5 text-sm text-muted-foreground">
                                        <ImageIcon className="mb-4 h-8 w-8" />
                                        Related artworks will appear here as the catalog grows.
                                    </div>
                                )}
                            </div>
                        </section>

                        {moreArtworks.length > 0 && (
                            <section className="mt-12 border-t pt-8">
                                <div className="mb-6 flex items-center justify-between gap-4">
                                    <h2 className="text-sm font-semibold uppercase tracking-normal">Related Artworks</h2>
                                    <div className="flex items-center gap-2">
                                        <Button asChild variant="ghost" className="rounded-sm">
                                            <Link href={primaryArtist?.slug ? `/artists/${primaryArtist.slug}` : '/discover'}>
                                                View All <ChevronRight className="ml-1 h-4 w-4" />
                                            </Link>
                                        </Button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
                                    {moreArtworks.map(artwork => {
                                        const imageUrl = getArtworkImageUrl(artwork, 'md');
                                        return (
                                            <Link key={artwork.id} href={`/artworks/${artwork.id}`} className="group min-w-0">
                                                <div className="relative aspect-square overflow-hidden rounded-sm border bg-muted">
                                                    <Image
                                                        src={imageUrl}
                                                        alt={artwork.title}
                                                        fill
                                                        sizes="(max-width: 640px) 50vw, 20vw"
                                                        className="object-cover transition group-hover:scale-105"
                                                        unoptimized={shouldBypassNextImageOptimization(imageUrl)}
                                                    />
                                                </div>
                                                <h3 className="mt-3 truncate text-sm font-semibold group-hover:underline">{artwork.title}</h3>
                                                <p className="truncate text-sm text-muted-foreground">{artwork.artistNames?.join(', ')}</p>
                                                {artwork.year && <p className="mt-1 text-xs text-muted-foreground">{artwork.year}</p>}
                                            </Link>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        <section className="mt-12 grid grid-cols-1 gap-6 rounded-md border bg-muted/10 p-6 md:grid-cols-3 md:divide-x">
                            <div className="flex gap-5 md:pr-6">
                                <Users className="mt-1 h-9 w-9 text-primary" />
                                <div>
                                    <h2 className="text-xs font-semibold uppercase tracking-normal">Owned By</h2>
                                    <p className="mt-1 font-semibold">{collectorCount} collectors</p>
                                    <Link href="/discover" className="mt-3 inline-flex items-center text-sm hover:underline">
                                        View All <ChevronRight className="ml-1 h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                            <div className="flex gap-5 md:px-6">
                                <TrendingUp className="mt-1 h-9 w-9 text-primary" />
                                <div>
                                    <h2 className="text-xs font-semibold uppercase tracking-normal">Market Insights</h2>
                                    <p className="mt-1 text-sm text-foreground/80">Last listed: {priceFormatted || 'Not recorded'}</p>
                                    <p className="text-sm text-foreground/80">Edition: {item.editionSize || 'Open'}</p>
                                    {item.sourceUrl && (
                                        <Link href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center text-sm hover:underline">
                                            View source <ChevronRight className="ml-1 h-4 w-4" />
                                        </Link>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-5 md:pl-6">
                                <MessageSquare className="mt-1 h-9 w-9 text-primary" />
                                <div>
                                    <h2 className="text-xs font-semibold uppercase tracking-normal">Collector Notes</h2>
                                    <p className="mt-1 text-sm leading-6 text-foreground/80">
                                        {collectionDetails?.notes || 'Add a private note to preserve condition, provenance, and personal context.'}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!user) {
                                                toast({ title: "Sign in required", description: "Log in to view private notes.", variant: "destructive" });
                                                return;
                                            }
                                            if (!isInCollection) {
                                                toast({ title: "Add to collection first", description: "Private notes are attached to artworks in your vault." });
                                                return;
                                            }
                                            setIsEditDialogOpen(true);
                                        }}
                                        className="mt-3 inline-flex items-center text-sm hover:underline"
                                    >
                                        View All Notes <ChevronRight className="ml-1 h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </section>
                    </div>
                </main>
                <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                    <DialogContent className="sm:max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Edit Private Details</DialogTitle>
                            <DialogDescription>
                                Update the private information for &quot;{item.title}&quot;. This is only visible to you.
                            </DialogDescription>
                        </DialogHeader>
                        <EditCollectionDetailsForm
                            details={collectionDetails}
                            onSave={handleSaveDetails}
                            onCancel={() => setIsEditDialogOpen(false)}
                            isSaving={isSubmitting}
                        />
                    </DialogContent>
                </Dialog>
                {isLightboxOpen && (
                    <Lightbox
                        images={allImages.length > 0 ? allImages : [primaryImageUrl]}
                        artists={artists}
                        moreArtworks={moreArtworks}
                        startIndex={lightboxIndex}
                        onClose={() => setIsLightboxOpen(false)}
                    />
                )}
                <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This action cannot be undone. This will permanently delete the artwork &quot;{item.title}&quot; and all of its associated private data.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDeletePermanently}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
                <Footer />
            </div>
        </>
    );
}
